import { Link } from 'react-router-dom';
import { Color, PARTS_QUOTE_STEPS, Size, heldDraftPill, t, type PartsQuoteStep, type StringKey } from '@bms2/shared';
import { Button, StatusPill } from '../../components/controls';
import { useStatusResolver } from '../../shell/StatusResolver';
import type { LatestQuoteView } from './DealStageCard';
import styles from './DealQuoteState.module.css';

/** The Parts page's Quote state as the answer carries it (decision 2390). */
export interface PartsQuoteView {
  quote: LatestQuoteView | null;
  step: PartsQuoteStep | null;
}

const STEP_WORDS: Record<PartsQuoteStep, StringKey> = {
  Draft: 'deal.parts.quoteStep.draft',
  Sent: 'deal.parts.quoteStep.sent',
  Accepted: 'deal.parts.quoteStep.accepted',
};

/**
 * 🔒 The Parts page's Quote state (piece 18, *The Deal page by type*; decision
 * 2390): Draft, Sent and Accepted, the newest Quote's step marked, drawn while
 * the Deal is open. The accepted Quote wins the Deal (decision 2091), and the
 * Won card stands in this band's place from then on. A Quote declined, voided
 * or expired marks no step, and its pill says which. 🔒 Draft Quote is the
 * page's own move (decision 2293), offered where no Quote stands at a step.
 */
export function DealQuoteState({ view, onDraftQuote = null }: { view: PartsQuoteView; onDraftQuote?: (() => void) | null }) {
  const resolveStatus = useStatusResolver();
  const at = view.step === null ? -1 : PARTS_QUOTE_STEPS.indexOf(view.step);
  const held = view.quote ? heldDraftPill(view.quote.status, view.quote.approvalStatus) : null;
  return (
    <section className={styles.band} data-deal-quote-state={view.step ?? 'none'}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('deal.parts.quoteBand')}</h3>
        <ol className={styles.steps} data-deal-quote-steps="true">
          {PARTS_QUOTE_STEPS.map((step, index) => {
            const state = at < 0 ? 'todo' : index < at || (index === at && step === 'Accepted') ? 'done' : index === at ? 'now' : 'todo';
            return (
              <li key={step} className={styles.step} data-quote-step={step} data-quote-step-state={state}>
                <span>{t(STEP_WORDS[step])}</span>
                {state === 'done' ? <span>{t('deal.request.stepDone')}</span> : state === 'now' ? <span>{t('deal.request.stepNow')}</span> : null}
              </li>
            );
          })}
        </ol>
      </div>
      {view.quote ? (
        <div className={styles.quote} data-deal-quote-state-quote={view.quote.id}>
          <Link to={`/quotes/${view.quote.id}`}>{view.quote.number}</Link>
          <StatusPill entityType="QuoteStatus" statusValue={view.quote.status} resolve={held ? () => ({ label: held.text, colorGroup: held.group }) : resolveStatus} size={Size.Small} />
        </div>
      ) : null}
      <p className={styles.hint}>{t(view.quote ? 'deal.parts.quoteHint' : 'deal.parts.quoteNone')}</p>
      {onDraftQuote && view.step === null ? (
        <span data-deal-quote-state-action="draft">
          <Button size={Size.Small} color={Color.Navy} text={[t('deal.quote.draft')]} actions={[onDraftQuote]} />
        </span>
      ) : null}
    </section>
  );
}
